import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import React, { Component } from 'react';
import ItemListContext from './ItemListContext';
import { isComboKey, isLetterKey, isNumberKey, isSpecialKey, modulo, scrollIntoView } from './utils';
var itemListId = 0;

var ItemList = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ItemList, _Component);

  function ItemList() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _Component.call.apply(_Component, [this].concat(args)) || this;

    _defineProperty(_assertThisInitialized(_this), "controllers", []);

    _defineProperty(_assertThisInitialized(_this), "highlightedIndex", null);

    _defineProperty(_assertThisInitialized(_this), "id", "item-list-" + itemListId++);

    _defineProperty(_assertThisInitialized(_this), "items", []);

    _defineProperty(_assertThisInitialized(_this), "searchString", '');

    _defineProperty(_assertThisInitialized(_this), "getItemId", function (index) {
      return index === null ? null : _this.id + "-item-" + index;
    });

    _defineProperty(_assertThisInitialized(_this), "getItemNode", function (index) {
      return index === null ? null : _this.items[index].getNode();
    });

    _defineProperty(_assertThisInitialized(_this), "getItemByText", function (text) {
      for (var index = 0; index < _this.items.length; index++) {
        var item = _this.items[index];
        var itemText = item.props.text || String(item.props.data);

        if (!item.props.disabled && itemText.toLowerCase().indexOf(text) === 0) {
          return item;
        }
      }

      return null;
    });

    _defineProperty(_assertThisInitialized(_this), "subscribeController", function (controllerInstance) {
      _this.controllers.push(controllerInstance);

      return function () {
        _this.controllers.splice(_this.controllers.indexOf(controllerInstance), 1);
      };
    });

    _defineProperty(_assertThisInitialized(_this), "subscribeItem", function (itemInstance) {
      _this.items.push(itemInstance);

      return function () {
        // if item was highlighted we need to null the highlightedIndex on unsubscribe
        if (itemInstance.props.index === _this.highlightedIndex) {
          _this.highlightedIndex = null;
        }

        _this.items.splice(_this.items.indexOf(itemInstance), 1);
      };
    });

    _defineProperty(_assertThisInitialized(_this), "scrollItemIntoViewByIndex", function (index) {
      var itemNode = _this.getItemNode(index);

      if (itemNode) {
        scrollIntoView(itemNode);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "stringSearch", function (event, delay) {
      if (delay === void 0) {
        delay = 300;
      }

      return new Promise(function (resolve) {
        if ((isNumberKey(event) || isLetterKey(event) || isSpecialKey(event)) && !isComboKey(event)) {
          event.preventDefault();

          _this.addToSearchString(event.key);

          _this.startSearchStringRefreshTimer(delay);

          resolve(_this.searchString);
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "addToSearchString", function (letter) {
      _this.searchString += letter.toLowerCase();
    });

    _defineProperty(_assertThisInitialized(_this), "clearSearchString", function () {
      _this.searchString = '';
    });

    _defineProperty(_assertThisInitialized(_this), "startSearchStringRefreshTimer", function (delay) {
      _this.clearSearchStringRefreshTimer();

      _this.stringSearchTimer = setTimeout(function () {
        _this.clearSearchString();
      }, delay);
    });

    _defineProperty(_assertThisInitialized(_this), "clearSearchStringRefreshTimer", function () {
      clearTimeout(_this.stringSearchTimer);
    });

    _defineProperty(_assertThisInitialized(_this), "getHighlightedIndex", function () {
      return _this.highlightedIndex;
    });

    _defineProperty(_assertThisInitialized(_this), "getHighlightedItemId", function () {
      return _this.highlightedItemId;
    });

    _defineProperty(_assertThisInitialized(_this), "setHighlightedIndex", function (index, _temp) {
      var _ref = _temp === void 0 ? {} : _temp,
          _ref$focus = _ref.focus,
          focus = _ref$focus === void 0 ? false : _ref$focus,
          _ref$maybeScrollIntoV = _ref.maybeScrollIntoView,
          maybeScrollIntoView = _ref$maybeScrollIntoV === void 0 ? true : _ref$maybeScrollIntoV;

      var event = {
        lastIndex: _this.highlightedIndex,
        newIndex: index
      };
      var item = _this.items[index];

      if (_this.items.length === 0) {
        return;
      }

      if (item && item.props.disabled) {
        index = null;
      }

      if (index !== null) {
        var itemNode = _this.getItemNode(index);

        if (maybeScrollIntoView) {
          scrollIntoView(itemNode);
        }

        if (focus) {
          itemNode.focus();
        }
      }

      if (_this.props.onHighlight) {
        _this.props.onHighlight(event);
      }

      _this.items.forEach(function (itemInstance) {
        if (itemInstance.props.index === index) {
          itemInstance.highlighted = true;
          itemInstance.forceUpdate();
        } else if (itemInstance.highlighted) {
          itemInstance.highlighted = false;
          itemInstance.forceUpdate();
        } else {
          itemInstance.highlighted = false;
        }
      });

      _this.controllers.forEach(function (controllerInstance) {
        controllerInstance.highlightedItemId = _this.getItemId(index);
        controllerInstance.forceUpdate();
      });

      _this.highlightedIndex = index;
      _this.highlightedItemId = _this.getItemId(index);
    });

    _defineProperty(_assertThisInitialized(_this), "moveHighlightedIndex", function (amount, _temp2) {
      var _ref2 = _temp2 === void 0 ? {} : _temp2,
          _ref2$contain = _ref2.contain,
          contain = _ref2$contain === void 0 ? true : _ref2$contain,
          focus = _ref2.focus,
          maybeScrollIntoView = _ref2.maybeScrollIntoView;

      var itemCount = _this.items.length;
      var index = _this.highlightedIndex;

      if (index === null) {
        index = amount >= 0 ? 0 : itemCount - 1;
      } else {
        index += amount;

        if (index < 0 || index >= itemCount) {
          index = contain ? modulo(_this.highlightedIndex + amount, itemCount) : null;
        }
      }

      var item = _this.items[index];

      if (item && item.props.disabled) {
        _this.highlightedIndex = index;

        _this.moveHighlightedIndex(amount, {
          contain: contain,
          focus: focus,
          maybeScrollIntoView: maybeScrollIntoView
        });
      } else {
        _this.setHighlightedIndex(index, {
          focus: focus,
          maybeScrollIntoView: maybeScrollIntoView
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "clearHighlightedIndex", function () {
      _this.setHighlightedIndex(null);
    });

    _defineProperty(_assertThisInitialized(_this), "selectItemByIndex", function (index) {
      var item = _this.items[index];

      if (item) {
        _this.props.onSelect(item.props.data, {
          index: item.props.index,
          node: item.node
        });

        if (item.props.onSelect) {
          item.props.onSelect(item.node);
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this), "selectItem", function (index) {
      _this.selectItemByIndex(index);
    });

    _defineProperty(_assertThisInitialized(_this), "selectHighlightedItem", function () {
      if (_this.highlightedIndex !== null) {
        _this.selectItemByIndex(_this.highlightedIndex);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "isItemSelected", function (data) {
      var selected = _this.props.selected;
      return typeof selected === 'function' ? selected(data) : selected === data;
    });

    return _this;
  } // String Search
  // Highlight
  // Select


  var _proto = ItemList.prototype;

  _proto.render = function render() {
    var children = this.props.children;
    return /*#__PURE__*/React.createElement(ItemListContext.Provider, {
      value: this
    }, typeof children === 'function' ? children(this) : children);
  };

  return ItemList;
}(Component);

export default ItemList;